/**
 * Voice Context Preloader - Phase 11.2 Latency Optimization
 * 
 * Pre-loads user context on app mount to eliminate cold-start latency
 * when executing voice tools. Reduces tool execution from ~300-500ms to <50ms.
 * 
 * Follows MPA (Musk-Pichai-Altman) architecture:
 * - Elon: Pre-load everything, zero latency for common operations
 * - Sundar: Shared infrastructure, single source of truth
 * - Sam: Learning-driven optimization
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';
import { recordLatency } from '@/utils/latency-monitor';

export interface VoiceContext {
  profile: {
    id: string;
    full_name: string | null;
    professional_title: string | null;
    timezone: string | null;
    // Phase 1: Comprehensive profile for AI intelligence
    bio: string | null;
    location: string | null;
    areas_of_expertise: string[] | null;
    skills: unknown | null;
    industry: string | null;
    experience_level: string | null;
    years_of_experience: number | null;
    current_employer: string | null;
    willing_to_relocate: boolean | null;
    preferences: unknown | null;
  } | null;
  contacts: Array<{
    id: string;
    name: string;
    title?: string;
  }>;
  privateContacts: Array<{
    id: string;
    first_name: string;
    last_name: string | null;
    relationship: string | null;
  }>;
  todayEvents: Array<{
    id: string;
    title: string;
    start_time: string;
    end_time: string;
    location?: string;
  }>;
  tomorrowEvents: Array<{
    id: string;
    title: string;
    start_time: string;
    end_time: string;
    location?: string;
  }>;
  // Extended calendar: 7 days of events for week view
  weekEvents: Array<{
    id: string;
    title: string;
    start_time: string;
    end_time: string;
    location?: string;
  }>;
  pendingTasks: Array<{
    id: string;
    title: string;
    due_date: string | null;
    priority: string;
  }>;
  // All user lists with their items for voice commands
  userLists: Array<{
    id: string;
    title: string;
    tags: string[];
    content?: string; // List items as content
  }>;
  isLoaded: boolean;
  lastRefresh: number;
}

const INITIAL_CONTEXT: VoiceContext = {
  profile: null,
  contacts: [],
  privateContacts: [],
  todayEvents: [],
  tomorrowEvents: [],
  weekEvents: [],
  pendingTasks: [],
  userLists: [],
  isLoaded: false,
  lastRefresh: 0,
};

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

export function useVoiceContextPreloader() {
  const { user, loading: authLoading } = useAuthContext();
  const [context, setContext] = useState<VoiceContext>(INITIAL_CONTEXT);
  const [isLoading, setIsLoading] = useState(false);
  const loadingRef = useRef(false);
  const isAuthenticated = !!user && !authLoading;

  const loadContext = useCallback(async (force = false) => {
    if (!user?.id) return;
    
    // Skip if already loading or recently loaded (unless forced)
    if (loadingRef.current) return;
    if (!force && context.isLoaded && Date.now() - context.lastRefresh < CACHE_DURATION) {
      return;
    }

    loadingRef.current = true;
    setIsLoading(true);
    const startTime = Date.now();

    try {
      // PARALLEL queries for maximum speed (Elon's principle)
      const [
        profileResult,
        networkContactsResult,
        privateContactsResult,
        todayEventsResult,
        tomorrowEventsResult,
        weekEventsResult,
        pendingTasksResult,
        userListsResult,
      ] = await Promise.all([
        // Profile with COMPREHENSIVE data for AI intelligence (Phase 1)
        supabase
          .from('profiles')
          .select(`
            id, full_name, professional_title, timezone,
            bio, location, areas_of_expertise, skills,
            industry, experience_level, years_of_experience,
            current_employer, willing_to_relocate, preferences
          `)
          .eq('id', user.id)
          .single(),
        
        // Network contacts (user_connections)
        supabase
          .from('user_connections')
          .select(`
            id,
            requester:profiles!user_connections_requester_id_fkey(id, full_name, professional_title),
            recipient:profiles!user_connections_recipient_id_fkey(id, full_name, professional_title)
          `)
          .or(`requester_id.eq.${user.id},recipient_id.eq.${user.id}`)
          .eq('status', 'accepted')
          .limit(50),
        
        // Private contacts
        supabase
          .from('private_contacts')
          .select('id, first_name, last_name, relationship')
          .eq('user_id', user.id)
          .order('times_mentioned', { ascending: false })
          .limit(30),
        
        // Today's events
        (() => {
          const todayStart = new Date();
          todayStart.setHours(0, 0, 0, 0);
          const todayEnd = new Date();
          todayEnd.setHours(23, 59, 59, 999);
          
          return supabase
            .from('life_events')
            .select('id, title, start_time, end_time, location')
            .eq('user_id', user.id)
            .gte('start_time', todayStart.toISOString())
            .lte('start_time', todayEnd.toISOString())
            .order('start_time', { ascending: true })
            .limit(20);
        })(),
        
        // Tomorrow's events (for calendar NLU - "what's on my schedule tomorrow")
        (() => {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          const tomorrowStart = new Date(tomorrow);
          tomorrowStart.setHours(0, 0, 0, 0);
          const tomorrowEnd = new Date(tomorrow);
          tomorrowEnd.setHours(23, 59, 59, 999);
          
          return supabase
            .from('life_events')
            .select('id, title, start_time, end_time, location')
            .eq('user_id', user.id)
            .gte('start_time', tomorrowStart.toISOString())
            .lte('start_time', tomorrowEnd.toISOString())
            .order('start_time', { ascending: true })
            .limit(20);
        })(),
        
        // Week's events (7 days for "this week" queries)
        (() => {
          const weekStart = new Date();
          weekStart.setHours(0, 0, 0, 0);
          const weekEnd = new Date();
          weekEnd.setDate(weekEnd.getDate() + 7);
          weekEnd.setHours(23, 59, 59, 999);
          
          return supabase
            .from('life_events')
            .select('id, title, start_time, end_time, location')
            .eq('user_id', user.id)
            .gte('start_time', weekStart.toISOString())
            .lte('start_time', weekEnd.toISOString())
            .order('start_time', { ascending: true })
            .limit(50);
        })(),
        
        // Pending tasks
        supabase
          .from('quick_notes')
          .select('id, title, due_date, priority')
          .eq('user_id', user.id)
          .eq('is_task', true)
          .eq('is_completed', false)
          .order('due_date', { ascending: true, nullsFirst: false })
          .limit(30),
        
        // ALL user lists with content (for dynamic list support)
        supabase
          .from('quick_notes')
          .select('id, title, tags, content')
          .eq('user_id', user.id)
          .not('tags', 'is', null)
          .order('updated_at', { ascending: false })
          .limit(50),
      ]);

      // Process network contacts
      const contacts = (networkContactsResult.data || []).map((conn: any) => {
        const requester = Array.isArray(conn.requester) ? conn.requester[0] : conn.requester;
        const recipient = Array.isArray(conn.recipient) ? conn.recipient[0] : conn.recipient;
        const isRequester = requester?.id === user.id;
        const contact = isRequester ? recipient : requester;
        return {
          id: contact?.id,
          name: contact?.full_name || '',
          title: contact?.professional_title || undefined,
        };
      }).filter((c: any) => c.id && c.name);

      const loadTime = Date.now() - startTime;
      recordLatency('context_load', loadTime);

      // Process user lists with content for dynamic list support
      const userLists = (userListsResult.data || []).map((note: any) => ({
        id: note.id,
        title: note.title || '',
        tags: note.tags || [],
        content: note.content || '',
      }));

      setContext({
        profile: profileResult.data,
        contacts,
        privateContacts: privateContactsResult.data || [],
        todayEvents: todayEventsResult.data || [],
        tomorrowEvents: tomorrowEventsResult.data || [],
        weekEvents: weekEventsResult.data || [],
        pendingTasks: pendingTasksResult.data || [],
        userLists,
        isLoaded: true,
        lastRefresh: Date.now(),
      });

      console.log(`[VoiceContextPreloader] Loaded context in ${loadTime}ms:`, {
        contacts: contacts.length,
        privateContacts: privateContactsResult.data?.length || 0,
        todayEvents: todayEventsResult.data?.length || 0,
        tomorrowEvents: tomorrowEventsResult.data?.length || 0,
        weekEvents: weekEventsResult.data?.length || 0,
        tasks: pendingTasksResult.data?.length || 0,
        lists: userLists.length,
        timezone: profileResult.data?.timezone,
      });

    } catch (error) {
      console.error('[VoiceContextPreloader] Failed to load context:', error);
    } finally {
      loadingRef.current = false;
      setIsLoading(false);
    }
  }, [user?.id, context.isLoaded, context.lastRefresh]);

  // Auto-load on mount when authenticated
  useEffect(() => {
    if (isAuthenticated && user?.id && !context.isLoaded) {
      loadContext();
    }
  }, [isAuthenticated, user?.id, context.isLoaded, loadContext]);

  // Refresh context when app comes back to focus
  useEffect(() => {
    const handleFocus = () => {
      if (isAuthenticated && context.isLoaded) {
        const timeSinceRefresh = Date.now() - context.lastRefresh;
        if (timeSinceRefresh > CACHE_DURATION) {
          loadContext(true);
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isAuthenticated, context.isLoaded, context.lastRefresh, loadContext]);

  return {
    context,
    isLoading,
    refresh: () => loadContext(true),
    isStale: context.isLoaded && Date.now() - context.lastRefresh > CACHE_DURATION,
  };
}
